import ROLES from './roles';
import { Data, Location, MultiSelectDataProps, rawDataGroup, Values } from '../profile/profile.types';

/** This method is used to organize multi-language data for select component data.
 *  It will return an organize array of object contains label and values
 *  @param res Array
 *  @param valueAsName By default it is false, used to store name in value instead of _id
 *  @returns Array
 */
export const OrganizeSelectData = (res: [], valueAsName = false) => {
  if (!res) return [];
  return res.map((item: { _id: string; name: { en: string; ur: string } }) => ({
    label: item.name.en,
    value: valueAsName ? item.name.en : item._id,
  }));
};

/** This method is used to organize single-language data for select component data.
 *  It will return an organize array of object contains label and values
 *  @param res Array
 *  @param valueAsName By default it is false, used to store name in value instead of _id
 *  @returns Array
 */
// eslint-disable-next-line
export const OrganizeSingleLanguageData = (res: any[], valueAsName = false): { label: string; value: string }[] => {
  if (!res) return [];
  return res.map((item: { _id: string; name: string | { en: string; ur: string } }) => {
    const name = typeof item.name === 'object' ? item?.name?.en + ' - ' + item?.name?.ur : item.name;
    return {
      label: name,
      value: valueAsName ? name : item._id,
    };
  });
};

/** This method is used to get name of data from id field.
 *  It will return the name of given id after searching in given data.
 *  @param data Array of object with label and value as object keys.
 *  @param selectedId string containing id.
 *  @returns string containing name.
 */
export const getNameFromId = (data: { label: string; value: string }[], selectedId: string | null) => {
  let foundName = '';
  data.map(key => {
    if (key.value === selectedId || key.label === selectedId) {
      foundName = key.label;
    }
  });
  return foundName;
};
/**
 * This function is being used to check if string array is empty or not
 * @param target string Array
 * @returns boolean
 */
export const isArrayEmpty = (target: string[]) => {
  return target.length === 0 || target === null;
};

/**
 * This function is being used to convert string array to object array.
 * e.g; ["KPK", "PUNJAB"] will be converted into [{name: "KPK"}, {name: "PUNJAB"}]
 * @param arr String Array
 * @returns Object Array
 */
export const stringArrayToObject = (arr: string[]) => (arr.length === 0 ? [] : arr.map(value => ({ name: value })));

/** This function is used to convert string date to this format Feb 24, 2022
 * @param date Date as string
 */
export const formateDateToMonthDayYear = (date: string) => {
  if (date === undefined || date === '') return '';
  else {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    }).format(new Date(date));
  }
};
/**
 * This function will be used as a debounce function. If a function is invoked frequently then it may degrade our web app performance.
 * It will be used to make sure this not happend. It will invoked a function after @wait time.
 * @param func Function name which will be debounce
 * @param wait Delay time after function is invoked
 * @returns
 */
export const debounce = <T extends (...args: any[]) => ReturnType<T>>(func: T, wait: number): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      func(...args);
    }, wait);
  };
};

/**
 * This function is used to change date format to dd mm yyyy
 * e.g, store date: 2022-01-25T13:18:57.761+00:00
 * Date after formate: 25 Jan 2022
 */
export const formatter = (date: string) =>
  new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).format(Date.parse(date));

/**
 * This function is used to change text format to capitalize (first letter capital and the rest small)
 * e.g, ANNOUNCEMENT to Announcement or CROP CALENDAR to Crop Calendar
 */
export const toCapitalize = (text: string) => {
  let formatedText = '';
  const arr = text.split(' ');
  arr.map(item => {
    if (item !== '') formatedText += item.charAt(0).toUpperCase() + item.slice(1).toLowerCase() + ' ';
  });
  return formatedText;
};

/*
 * This function is used to return and seperator for filter query
 * @param value filter as string
 * @returns string
 */
export const insertAndSeperator = (value: string) => (value.split('=')[1] !== '' ? ' and ' : '');

/**
 * This function is used to convert full mobile with country code to simple number.
 * Like +923401234567 will be converted to 03401234567
 * @param number Number
 */
export const convert92NumberToZero = (number: string) => {
  if (number.includes('+92')) {
    return '0'.concat(number.substring(3));
  } else return number;
};

/**
 * This function is being used to validate password.
 * Regex: Password must contain 10 characters, at least one uppercase, one lowercase, one number and one special case character
 * @param value Password
 * @returns false/true
 */
export const validatePassword = (value: string | undefined) => {
  const regex = new RegExp(/^.*(?=.{8,})((?=.*[!@#$%^&*()\-_=+{};:,<.>]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/);
  if (value !== undefined) {
    return !!value?.match(regex);
  } else return true;
};
/**
 * This function is used to return the extension of a file
 * @param filename complete file name
 * @returns extension of a file
 */
export const getFileExtension = (filename: string) => {
  const ext = /^.+\.([^.]+)$/.exec(filename);
  return ext === null ? '' : ext[1];
};

/**
 * This function is used to filter out the valid roles from login user roles array
 * @param roles Login user all roles
 * @returns roles
 */
export const getUserRoles = (roles: string[]) => [...(roles || '')]?.filter(r => ROLES.includes(r));

/**
 * This function is used to generate some random id
 * @returns ID
 */
export const generateRandomID = () => (Math.random() + 1).toString(36).substring(2);

/**
 * This function is used to calculate the time since the @date we provide
 * @param date Date as a string
 * @returns time as a string
 */
export const timeSince = (date: string) => {
  const targetDate = new Date(date);
  const seconds = Math.floor((+new Date() - +targetDate) / 1000);

  let interval = seconds / 31536000;

  if (interval >= 1) {
    return Math.floor(interval) + ' years ago';
  }
  interval = seconds / 2592000;
  if (interval >= 1) {
    return Math.floor(interval) + ' months ago';
  }
  interval = seconds / 86400;
  if (interval >= 1) {
    const day =
      Math.floor(interval) === 1
        ? `Yesterday at ${targetDate.toLocaleTimeString()}`
        : `${Math.floor(interval)} days ago at ${targetDate.toLocaleTimeString()}`;
    return day;
  }
  interval = seconds / 3600;
  if (interval >= 1) {
    const hr = Math.floor(interval);
    return hr === 1 ? hr + ' hour ago' : hr + ' hours ago';
  }
  interval = seconds / 60;
  if (interval > 1) {
    return Math.floor(interval) + ' mins ago';
  }
  interval = seconds / 60;
  if (interval < 1) {
    return 'Just Now';
  }
  // return Math.floor(seconds) + ' seconds';
  return 'Just Now';
};

/**
 * Return today date with input format
 * @param format string
 * @returns formated today date
 */
export const getTodayDate = (format: 'YYYYMMDD'): string => {
  const date = new Date();

  switch (format) {
    case 'YYYYMMDD': {
      const Date = date.getDate();
      const month = date.getMonth() + 1;
      const formatedMonth = month < 10 ? '0' + String(month) : String(month);
      const formatedDate = Date < 10 ? '0' + String(Date) : String(Date);
      return String(date.getFullYear()) + formatedMonth + formatedDate;
    }
    default:
      return date.toISOString();
  }
};

/**
 * @param string takes type string
 * @return boolean
 */
const isValidJsonObject = (string: string) => {
  try {
    JSON.parse(string);
  } catch (ex) {
    return false;
  }
  return true;
};

/**
 * Gets URL String and return Object
 * @param $filter
 * @returns
 */
export const queryArrayToObjects = ($filter: string): Object => {
  // identifying operator: and/or & separating filters into [filters:string] or [filter:string]
  const opr = $filter.includes(' or ') ? ' or ' : $filter.includes(' and ') ? ' and ' : undefined;
  const filters: string[] = opr ? $filter.split(opr) : [$filter];
  const obj: unknown | any = {};
  const filterObjects: Object[] = filters.map(filter => {
    const [field, _, ...valueArray] = filter.split(' ');
    let value = valueArray.join(' ').trim();
    if (isValidJsonObject(value)) value = JSON.parse(value);
    if (field.includes('.')) {
      obj[field.replace('.', '_')] = value;
    } else {
      obj[field] = value;
    }
    return obj;
  });
  return filterObjects[0];
};

// Utility function to check if the string is a valid JSON object
function isValidJsonObjectDupKey(value: string): boolean {
  try {
    JSON.parse(value);
    return typeof JSON.parse(value) === 'object';
  } catch (e) {
    return false;
  }
}

export const queryArrayToObjectsDupKey = ($filter: string): Object => {
  // Identifying operator: 'and' or 'or' & separating filters into [filters: string] or [filter: string]
  const opr = $filter.includes(' or ') ? ' or ' : $filter.includes(' and ') ? ' and ' : undefined;

  const filters: string[] = opr ? $filter.split(opr) : [$filter];

  const obj: unknown | any = {};
  const filterObjects: Object[] = filters.map(filter => {
    const [field, _, ...valueArray] = filter.split(' ');

    let value = valueArray.join(' ').trim();

    // Replace dots with underscores early if the field is nested (e.g., 'current_crops.sowing_date')
    const modifiedField = field.includes('.') ? field.replace('.', '_') : field;

    if (isValidJsonObjectDupKey(value)) {
      value = JSON.parse(value);
    }

    // Check if the key already exists and add a suffix if necessary
    let key = modifiedField;
    let counter = 1;

    // Ensure the key is unique by adding _1, _2, etc.
    while (Object.prototype.hasOwnProperty.call(obj, key)) {
      key = `${modifiedField}_${counter}`;
      counter++;
    }

    // Assign the value to the unique key
    obj[key] = value;

    return obj;
  });

  return filterObjects[0];
};

export const changeParamsToObjectFormat = (findKey: string, replaceValue: string, entries: unknown | any) => {
  const result: unknown | any = {};
  let keyfoundStatus: Boolean = false;
  for (const [key, value] of entries) {
    if (key === findKey) {
      result[key] = replaceValue;
      keyfoundStatus = true;
    } else {
      result[key] = value;
    }
  }
  if (keyfoundStatus === false) {
    result[findKey] = replaceValue;
  }
  return result;
};

/**
 * This function will convert and/or with .* and *. in FilterValues
 * @param query
 * @returns string
 */

export const replaceQueryWithSpecificWords = (query: string) => {
  if (query.includes(' and ')) return query.replace(' and ', ' .* ');
  else if (query.includes(' or ')) return query.replace(' or ', ' *. ');
  return query;
};

/**
 * This will convert .* and *. with and/or in FilterValues
 * @param query
 * @returns string
 */

export const reverseQueryWithSpecificWords = (query: string) => {
  if (query.includes(' .* ')) return query.replace(' .* ', ' and ');
  else if (query.includes(' *. ')) return query.replace(' *. ', ' or ');
  return query;
};

/**This Function Help in filtering location Data
*Province, Division, District, Tehsils
*User Profile, DEOS in DES, AA in AA, Users in OM, FCs in CM

*it takes 6 parameters,
*@loc value selected from dropdown
*@flag which value is selected, province, division etc,
*@value current selected values from all dropdowns
*@updateValue function to update selected values
*@spreaded_Obj list of dropdown values and function to update those values
*@spreaded_Obj list of all names of districts, divisions, tehsils
*/
export const trickleData = (
  loc: string[] | string,
  flag: string,
  value: Values,
  updateValue: (flag: string, value: string | undefined) => void,
  { districtsData, setDistrictsData, divisionsData, setDivisionData, setTehsilsData }: Data,
  { districts, divisions, tehsils }: rawDataGroup
) => {
  let divisionsArray: MultiSelectDataProps[] = [];
  let districtsArray: MultiSelectDataProps[] = [];
  let tehsilsArray: MultiSelectDataProps[] = [];
  //Check if value is selected in dropdown or clared.
  if (loc !== '') {
    //check where value is updated in this case province
    if (flag === 'province') {
      //creatting new dropdown values on the basis of selected value
      divisionsArray = OrganizeSingleLanguageData(divisions.filter((d: Location) => d?.province?.name === loc));
      //creating new drowpdown values on the basis of new division dropdown values
      districtsArray = OrganizeSingleLanguageData(
        divisionsArray.map((obj: { label: string; value: string }) => districts.filter((d: any) => d?.division.name === obj.label)).flat()
      );
      //creating new drowpdown values on the basis of new district dropdown values
      tehsilsArray = OrganizeSingleLanguageData(
        districtsArray.map((obj: { label: string; value: string }) => tehsils.filter((d: any) => d?.district.name === obj.label)).flat()
      );
      //assigning new values dropdown values to dropdown
      setDivisionData(divisionsArray);
      setDistrictsData(districtsArray);
      setTehsilsData(tehsilsArray);
      //checking if selected division exists in new selected province, if no then clear selected division and so on.
      if (!divisionsArray.find((div: any) => div.value === value.division)) updateValue('division', '');
      if (!districtsArray.find((div: any) => div.value === value.district)) updateValue('district', '');
      if (!tehsilsArray.find((div: any) => div.value === value.tehsil)) updateValue('tehsil', '');
    } //check if value updated at division
    else if (flag === 'division') {
      //creatting new dropdown values on the basis of selected value
      districtsArray = OrganizeSingleLanguageData(districts.filter((d: Location) => d?.division?.name === loc));
      //creating new drowpdown values on the basis of new district dropdown values
      tehsilsArray = OrganizeSingleLanguageData(
        districtsArray.map((obj: { label: string; value: string }) => tehsils.filter((d: any) => d?.district.name === obj.label)).flat()
      );
      //assigning new values dropdown values to dropdown
      setDistrictsData(districtsArray);
      setTehsilsData(tehsilsArray);
      //REVERSE ENGINEERING, BOTTOM TO TOP
      //updating province value in the basis of selected division
      const dis = divisions.find((d: Location) => d?.name === loc);
      updateValue('province', dis?.province?._id);
      //checking if selected division exists in new selected province, if now then clear selected division and so on.
      if (!districtsArray.find((div: any) => div.value === value.district)) updateValue('district', '');
      if (!tehsilsArray.find((div: any) => div.value === value.tehsil)) updateValue('tehsil', '');
    } //check if value updated at district
    else if (flag === 'district') {
      //creatting new dropdown values on the basis of selected value
      tehsilsArray = OrganizeSingleLanguageData(tehsils.filter((d: Location) => d?.district?.name === loc));
      setTehsilsData(tehsilsArray);
      //REVERSE ENGINEERING, BOTTOM TO TOP
      const div = districts.find((d: Location) => d?.name === loc);
      const dis = divisions.find((d: Location) => d?.name === div?.division?.name);
      updateValue('division', div?.division?._id);
      updateValue('province', dis?.province?._id);
      //checking if selected tehsil exists in new selected district, if no then clear selected division and so on.
      if (!tehsilsArray.find((div: any) => div.value === value.tehsil)) updateValue('tehsil', '');
    } //check if value updated at tehsil
    else if (flag === 'tehsil') {
      //REVERSE ENGINEERING, BOTTOM TO TOP
      const teh = tehsils.find((d: Location) => d?.name === loc);
      const div = districts.find((d: Location) => d?.name === teh?.district?.name);
      const dis = divisions.find((d: Location) => d?.name === div?.division?.name);
      updateValue('district', teh?.district?._id);
      updateValue('division', div?.division?._id);
      updateValue('province', dis?.province?._id);
    }
  } //if selected location is empty, cleared
  else {
    //Location is cleared at district
    if (flag === 'district') {
      console.log('district', value);
      //if division is not empty, update tehsil on the dropdown values of district
      if (value.division !== '' && value.division !== null) {
        tehsilsArray = OrganizeSingleLanguageData(
          districtsData.map((obj: { label: string; value: string }) => tehsils.filter((d: any) => d?.district.name === obj.label)).flat()
        );
        setTehsilsData(tehsilsArray);
      } //if province is not empty, update tehsil & districts on the dropdown values of province
      else if (value.province !== '' && value.province !== null) {
        districtsArray = OrganizeSingleLanguageData(
          divisionsData.map((obj: { label: string; value: string }) => districts.filter((d: any) => d?.division.name === obj.label)).flat()
        );
        tehsilsArray = OrganizeSingleLanguageData(
          districtsArray.map((obj: { label: string; value: string }) => tehsils.filter((d: any) => d?.district.name === obj.label)).flat()
        );
        setDistrictsData(districtsArray);
        setTehsilsData(tehsilsArray);
      } //if all dropdown empty, reset
      else {
        setDistrictsData(OrganizeSingleLanguageData(districts));
        setTehsilsData(OrganizeSingleLanguageData(tehsils));
      }
    } //Location is cleared at district
    else if (flag === 'division') {
      //if province is not empty, update tehsil & district on the dropdown values of province
      if (value.province !== '' && value.province !== null) {
        districtsArray = OrganizeSingleLanguageData(
          divisionsData.map((obj: { label: string; value: string }) => districts.filter((d: any) => d?.division.name === obj.label)).flat()
        );
        setDistrictsData(districtsArray);
        if (value.district === '' || value.district === null) {
          tehsilsArray = OrganizeSingleLanguageData(
            districtsArray.map((obj: { label: string; value: string }) => tehsils.filter((d: any) => d?.district.name === obj.label)).flat()
          );
          setTehsilsData(tehsilsArray);
        }
      } //if province empty, reset dropdown
      else {
        setDistrictsData(OrganizeSingleLanguageData(districts));
        setTehsilsData(OrganizeSingleLanguageData(tehsils));
      }
    } //Location is cleared at province
    else if (flag === 'province') {
      //reset dropdown for division
      setDivisionData(OrganizeSingleLanguageData(divisions));
      //if division empty, reset dropdown for district
      if (value.division === '' || value.division === null) {
        setDistrictsData(OrganizeSingleLanguageData(districts));
      }
      //if district empty, reset dropdown for tehsil
      if (value.district === '' || value.district === null) {
        setTehsilsData(OrganizeSingleLanguageData(tehsils));
      }
    }
  }
};

export function shortenName(fullName: string): string {
  // Split the full name into individual words
  const words: string[] = fullName.split(' ');

  // Create an acronym by taking the first letter of each word
  const acronym: string = words.map(word => word[0].toUpperCase()).join('');

  return acronym;
}

export function calculatePreviousDate(yearsAgo: number) {
  // Get the current date
  const currentDate = new Date();

  // Calculate the year for the previous date
  const previousYear = currentDate.getFullYear() - yearsAgo;

  // Create a new date with the calculated year
  const previousDate = new Date(previousYear, currentDate.getMonth(), currentDate.getDate());

  // Format the result as "YYYY-MM-DD" (you can customize the format if needed)
  const formattedPreviousDate = previousDate.toISOString().split('T')[0];

  return formattedPreviousDate;
}

/**
 * Calculate the difference in years between a previous date and the current date.
 * @param {string | number | Date} previousDate - The date to compare with the current date.
 * @returns {number} The difference in years between the previous date and the current date.
 */
export function calculateYearsDifference(previousDate: string | number | Date): number {
  // Parse the input date to a Date object
  const previousDateObject = new Date(previousDate);

  // Get the current date
  const currentDate: Date = new Date();

  // Calculate the difference in milliseconds
  const timeDifference = currentDate.getTime() - previousDateObject.getTime();

  // Calculate the number of milliseconds in a year
  const millisecondsInYear = 1000 * 60 * 60 * 24 * 365.25;

  // Calculate the number of years
  const yearsDifference = Math.floor(timeDifference / millisecondsInYear);

  return yearsDifference;
}

export function isValidIBANNumber(input: string): boolean {
  const CODE_LENGTHS: Record<string, number> = {
    AD: 24,
    AE: 23,
    AT: 20,
    AZ: 28,
    BA: 20,
    BE: 16,
    BG: 22,
    BH: 22,
    BR: 29,
    CH: 21,
    CR: 22,
    CY: 28,
    CZ: 24,
    DE: 22,
    DK: 18,
    DO: 28,
    EE: 20,
    ES: 24,
    FI: 18,
    FO: 18,
    FR: 27,
    GB: 22,
    GI: 23,
    GL: 18,
    GR: 27,
    GT: 28,
    HR: 21,
    HU: 28,
    IE: 22,
    IL: 23,
    IS: 26,
    IT: 27,
    JO: 30,
    KW: 30,
    KZ: 20,
    LB: 28,
    LI: 21,
    LT: 20,
    LU: 20,
    LV: 21,
    MC: 27,
    MD: 24,
    ME: 22,
    MK: 19,
    MR: 27,
    MT: 31,
    MU: 30,
    NL: 18,
    NO: 15,
    PK: 24,
    PL: 28,
    PS: 29,
    PT: 25,
    QA: 29,
    RO: 24,
    RS: 22,
    SA: 24,
    SE: 24,
    SI: 19,
    SK: 24,
    SM: 27,
    TN: 24,
    TR: 26,
    AL: 28,
    BY: 28,
    EG: 29,
    GE: 22,
    IQ: 23,
    LC: 32,
    SC: 31,
    ST: 25,
    SV: 28,
    TL: 23,
    UA: 29,
    VA: 22,
    VG: 24,
    XK: 20,
  };

  const iban = String(input)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
  const code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/);

  if (!code || iban.length !== CODE_LENGTHS[code[1]]) {
    return false;
  }

  const digits = (code[3] + code[1] + code[2]).replace(/[A-Z]/g, letter => String(letter.charCodeAt(0) - 55));

  return mod97(digits) === 1;
}

function mod97(string: string): number {
  let checksum = string.slice(0, 2);
  for (let offset = 2; offset < string.length; offset += 7) {
    const fragment = String(checksum) + string.substring(offset, offset + 7);
    checksum = String(parseInt(fragment, 10) % 97);
  }
  return parseInt(checksum, 10);
}

export function isEmptyValue(value: any) {
  return value === 0 ? '' : value;
}

// Function to get the date range for the past 7 days
export function getLast7DaysRange() {
  const today = new Date(); // Current date
  const startOfLast7Days = new Date(); // Create a new date object for 7 days ago
  startOfLast7Days.setDate(today.getDate() - 7); // Subtract 7 days from today's date

  return {
    start: today, // Start of the range (7 days ago)
    end: startOfLast7Days, // End of the range (today)
  };
}

// Function to calculate map center and zoom level based on path coordinates
// Function to calculate map center and zoom level based on path coordinates
interface Coordinates {
  lat: number;
  lng: number;
}

export const calculateMapCenterAndZoom = (pathCoordinates: Coordinates[] = []) => {
  // Ensure the maps API is loaded and available
  if (!window.google || !window.google.maps) {
    console.error('Google Maps API is not loaded.');
    return { center: { lat: 0, lng: 0 }, zoom: 12 }; // Return default values if maps not loaded
  }

  // Check if pathCoordinates is empty or undefined
  if (!Array.isArray(pathCoordinates) || pathCoordinates.length === 0) {
    return { center: { lat: 0, lng: 0 }, zoom: 12 };
  }

  // Calculate the bounds of the path coordinates
  const bounds = new window.google.maps.LatLngBounds();
  pathCoordinates.forEach((coord: Coordinates) => {
    bounds.extend(new window.google.maps.LatLng(coord.lat, coord.lng));
  });

  // Calculate the center of the bounds (midpoint of the path)
  const center = {
    lat: (bounds.getNorthEast().lat() + bounds.getSouthWest().lat()) / 2,
    lng: (bounds.getNorthEast().lng() + bounds.getSouthWest().lng()) / 2,
  };

  // Calculate the difference in lat/lng
  const latDiff = bounds.getNorthEast().lat() - bounds.getSouthWest().lat();
  const lngDiff = bounds.getNorthEast().lng() - bounds.getSouthWest().lng();

  // Calculate the zoom level based on the size of the bounding box
  const zoomLevel = 8 - Math.log(latDiff + lngDiff) / Math.LN2;

  // Cap the zoom level between 12 and 18
  const zoom = Math.max(12, Math.min(18, zoomLevel));

  return { center, zoom };
};

export function convertTo12HourFormat(time24: string): string {
  if (!time24) return 'Not Available'; // Handle missing or undefined input

  try {
    // Extract time portion from ISO 8601 format
    const timePart = time24.includes('T') ? time24.split('T')[1].split('.')[0] : time24;

    const [hours, minutes] = timePart.split(':');

    if (!hours || !minutes) return 'Invalid Time'; // Handle invalid time format

    let hoursInt = parseInt(hours, 10);
    const period = hoursInt >= 12 ? 'PM' : 'AM';

    // Convert hours from 24-hour format to 12-hour format
    hoursInt = hoursInt % 12 || 12; // Convert 0 or 12 to 12 for proper formatting

    return `${hoursInt}:${minutes} ${period}`;
  } catch (error) {
    console.error('Error parsing time:', error);
    return 'Invalid Time'; // Fallback for unexpected errors
  }
}
